/**
 * Servescape countdown bar — scheduling, timer styles, visibility.
 */
(function () {
  function parseDateTime(str) {
    if (!str || typeof str !== 'string') return null;
    const trimmed = str.trim();
    if (!trimmed) return null;

    const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    const [datePart, timePart = '00:00'] = normalized.split('T');
    const dateBits = datePart.split('-').map(Number);
    if (dateBits.length < 3 || dateBits.some(Number.isNaN)) return null;

    const timeBits = timePart.split(':').map(Number);
    const hours = timeBits[0] || 0;
    const minutes = timeBits[1] || 0;
    const seconds = timeBits[2] || 0;

    return new Date(dateBits[0], dateBits[1] - 1, dateBits[2], hours, minutes, seconds).getTime();
  }

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function formatDays(n) {
    return n < 10 ? pad2(n) : String(n);
  }

  class SSCountdownBar extends HTMLElement {
    constructor() {
      super();
      this.barEl = this.closest('.ss-countdown-bar');
      this.timerEl = this.querySelector('[data-ss-countdown-timer]');
      this.daysUnit = this.querySelector('[data-unit="days"]');
      this.daysSeparator = this.querySelector('[data-separator-days]');
      this.intervalId = null;
      this.lastDigits = { days: null, hours: null, minutes: null, seconds: null };
      this.showingDays = false;
      this.ended = false;
    }

    connectedCallback() {
      this.showMode = this.dataset.showMode || 'now';
      this.visibleAt = parseDateTime(this.dataset.visibleAt);
      this.endAt = parseDateTime(this.dataset.endAt);
      this.hideOnComplete = this.dataset.hideOnComplete === 'true';
      this.timerStyle = this.dataset.timerStyle || 'plain';

      this.tick();
      this.intervalId = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.intervalId) window.clearInterval(this.intervalId);
    }

    tick() {
      const now = Date.now();

      if (this.showMode === 'scheduled') {
        if (!this.visibleAt) {
          this.setHidden(true);
          return;
        }
        if (now < this.visibleAt) {
          this.setHidden(true);
          return;
        }
      }

      this.setHidden(false);

      if (!this.endAt || this.timerStyle === 'none') {
        return;
      }

      const remaining = this.endAt - now;

      if (remaining <= 0) {
        this.onComplete();
        return;
      }

      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      const showDays = days >= 1;
      const hours = showDays
        ? Math.floor((remaining / (1000 * 60 * 60)) % 24)
        : Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining / (1000 * 60)) % 60);
      const seconds = Math.floor((remaining / 1000) % 60);

      this.setDaysVisible(showDays);

      if (showDays) {
        this.updateUnit('days', days, formatDays);
      } else {
        this.lastDigits.days = null;
      }

      this.updateUnit('hours', hours, pad2);
      this.updateUnit('minutes', minutes, pad2);
      this.updateUnit('seconds', seconds, pad2);
    }

    setDaysVisible(showDays) {
      if (this.showingDays === showDays) return;
      this.showingDays = showDays;
      this.lastDigits.hours = null;

      if (this.daysUnit) this.daysUnit.hidden = !showDays;
      if (this.daysSeparator) this.daysSeparator.hidden = !showDays;
    }

    setHidden(hidden) {
      const target = this.barEl || this;
      target.classList.toggle('ss-countdown-bar--hidden', hidden);
      target.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    }

    onComplete() {
      if (this.ended) return;
      this.ended = true;

      if (this.timerEl) {
        this.timerEl.classList.add('ss-countdown-bar__timer--complete');
      }

      if (this.hideOnComplete) {
        const target = this.barEl || this;
        target.classList.add('ss-countdown-bar--ended');
      }

      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    updateUnit(unit, value, format) {
      const display = format(value);
      if (this.lastDigits[unit] === display) return;
      this.lastDigits[unit] = display;

      const root = this.querySelector(`[data-unit="${unit}"]`);
      if (!root) return;

      const valueEl = root.querySelector('[data-digit-value]');
      if (valueEl) valueEl.textContent = display;
    }
  }

  if (!customElements.get('ss-countdown-bar')) {
    customElements.define('ss-countdown-bar', SSCountdownBar);
  }
})();
